import React, { useContext, useEffect, useState } from 'react';
import uuid from 'uuid-random';
import { Flex, FlexBehavior, FlexWrap } from 'react-magma-dom';
import { ContainerButton } from './ContainerButton';
import {
  ContainerWrapper,
  FlexWrapper,
  ImageWithPositionWrapper,
  LineBreak,
  MarginWrapper,
  SpacedSpan,
  StyledEmergentContainer,
  StyledExemplarHeading,
  StyledInfoLabel,
} from '../../Styled';
import {
  ContentLine,
  EmergentContentSection,
  VisibilityState,
  EmergentExemplar,
  ExemplarWord,
  Image,
  ImageSide,
  WordsAndSentencesContent,
  WordsPageContent,
  WritingType,
  SelectedEmergentImage,
  ImageDimensions,
  WordsPageTitle,
} from '../../../types';
import {
  SENTENCES as SENTENCES_URL,
  WORDS as WORDS_URL,
  WORDS_AND_SENTENCES as WORDS_AND_SENTENCES_URL,
} from '../../../constants/navigation';
import { WordsScreen } from './Words/WordsScreen';
import { COLORS, SPECIAL_CHARACTERS, TEXT_TOOL_COLORS } from '../../../constants';
import { Verbs } from './Words/Verbs';
import {
  findEmergentContentByPage,
  findEmergentExemplarTitle,
  getExemplarImageURL,
  getStyles,
  urlToContentMap,
} from '../../../utils';
import { getSectionColor } from '../../../utils/exemplar';
import { StyledWordContext } from '../../../contexts/styledWordContext';
import { ExemplarContext } from '../../../contexts/emergentContext';
import { CLICKABLE_WORD, ClickableWord } from '../../ClickableWord';
import { DrawingPanel } from '../../DrawingPanel';
import { EditImage } from '../../Image';
import { InfoIcon } from '../../../assets/icons/InfoIcon';
import { EditToolContext } from '../../../contexts/editToolContext';
import { ReplaceImageModal } from '../ReplaceImageModal';
import { UndoRedoContext } from '../../../contexts/undoRedoContext';

export interface IEmergentScreen {
  exemplarId: number;
  dimensions: {
    width: number;
    height: number;
  };
  mode: string;
}

export const EmergentScreen: React.FC<IEmergentScreen> = (props): JSX.Element => {
  const { exemplarId, dimensions, mode } = props;

  const [isReplaceImageModalOpen, setReplaceImageModal] = useState(false);

  const [selectedImage, setSelectedImage] = useState<SelectedEmergentImage>({
    id: '',
    side: ImageSide.left,
    sectionId: '',
  });

  const {
    exemplarState,
    handleFontStyle,
    handleWordHighlight,
    saveHighlightsUpdates,
    updateRelationships,
    replaceImage,
    setSavedEdits,
  } = useContext(ExemplarContext);
  const { styledWordState, revealSentence, handleTool, handleChangeScreen } = useContext(StyledWordContext);
  const { isEditMode } = useContext(EditToolContext);
  const { isUndoneEventsNotEmpty, clearEvents } = useContext(UndoRedoContext);
  const relationshipsImages = exemplarState.relationships.images;
  const isHighlightingMode = styledWordState.isHighlightingMode;
  const isWordClickable = isEditMode && isHighlightingMode;

  useEffect(() => {
    if (isUndoneEventsNotEmpty) {
      clearEvents();
    }

    setSavedEdits(exemplarState, mode);
  }, [mode]);

  useEffect(() => {
    handleChangeScreen(styledWordState);
  }, [screen]);

  const handleUploadImage = (
    id: string,
    default_uuid: string | undefined,
    sectionId: string,
    dimensions: ImageDimensions,
    side?: ImageSide
  ) => {
    setSelectedImage({
      id,
      default_uuid,
      sectionId,
      dimensions,
      side,
    });
    setReplaceImageModal(true);
  };

  const renderWordsPageContent = (exemplarState: EmergentExemplar) => {
    const images = (exemplarState.content[0] as WordsPageContent).images || [];
    const labels = (exemplarState.content[0] as WordsPageContent).words || [];
    const labelColor = `#${(exemplarState.content[0] as WordsPageContent).label_color}`;
    const imagesWithIds = images.map(image => {
      return { ...image, url: getExemplarImageURL(image.uuid, relationshipsImages) };
    });

    return (
      <div>
        <WordsScreen
          images={imagesWithIds}
          labels={labels}
          labelColor={labelColor}
          editMode={isEditMode}
          styledWordState={styledWordState}
          relatedImages={relationshipsImages}
          exemplarId={exemplarId}
          updateRelationships={updateRelationships}
        />
        <Verbs labels={labels} isDisabled={styledWordState.imageTool.isVisible} />
      </div>
    );
  };

  const getWordsAndSentencesContent = (content: Array<WordsAndSentencesContent>, side: string) => {
    const correctLabel = findEmergentContentByPage(content, mode) as WordsAndSentencesContent;

    if (side === ImageSide.left) {
      return correctLabel?.content?.left;
    }

    return correctLabel?.content?.right;
  };

  const getFontColor = (word: ExemplarWord): string => {
    const identifier = mode === undefined ? word.sectionId : `${mode}${word.sectionId}`;
    const data = styledWordState.font[identifier];

    if (data && data[word.id] != undefined) {
      if (TEXT_TOOL_COLORS.includes(data[word.id].color)) {
        return data[word.id].color;
      }
    }

    if (word.color) {
      return word.color;
    }

    if (word.style?.includes('highlighted')) {
      return COLORS.usafaBlue;
    }

    return '';
  };

  const getMargin = (word: ExemplarWord): string => {
    if (word.wordIndex == 0) {
      return '0 0';
    }

    return !word.style?.includes('nospacing') ? '0px 0px 0px 16px' : '0 0';
  };

  const renderWords = (line: ContentLine, condition: string) => {
    const isHidden = condition !== 'visible' || styledWordState.isHiddenTextMode;

    return line.words.map((word: ExemplarWord) => {
      const wordStyles = getStyles(word?.style);

      // All special characters shouldn't be clickable
      if (SPECIAL_CHARACTERS.includes(word.text)) {
        return (
          <SpacedSpan
            className={CLICKABLE_WORD}
            margin={getMargin(word)}
            color={getFontColor(word)}
            wordStyles={wordStyles}
            key={uuid()}
            aria-label={`${word.text}`}
            isHidden={isHidden}
          >
            {word.text}
          </SpacedSpan>
        );
      }

      return (
        <ClickableWord
          page={mode}
          editMode={isEditMode}
          isClickable={isWordClickable}
          word={word}
          key={uuid()}
          languageHighlighted={false}
          handleWordHighlight={handleWordHighlight}
          saveHighlightsUpdates={saveHighlightsUpdates}
          handleTool={handleTool}
          handleFontStyle={handleFontStyle}
        >
          <SpacedSpan
            data-testid="spaced-span"
            className={CLICKABLE_WORD}
            margin={getMargin(word)}
            color={getFontColor(word)}
            wordStyles={wordStyles}
            key={uuid()}
            aria-label={`${word.text}`}
            isHidden={isHidden}
          >
            {word.text}
          </SpacedSpan>
        </ClickableWord>
      );
    });
  };

  const handleClick = (id: string): void => {
    if (!isEditMode) {
      revealSentence(id);
    }
  };

  const getStyledContainer = (
    section: EmergentContentSection | WordsPageTitle | string,
    condition: VisibilityState
  ): JSX.Element | string => {
    if (typeof section === 'string') {
      return section;
    }

    return (
      <>
        {section?.lines.map((line: ContentLine) => {
          if (!line.words.length) {
            return <LineBreak key={uuid()} />;
          }

          return (
            <Flex key={uuid()} behavior={FlexBehavior.container} wrap={FlexWrap.wrap}>
              {renderWords(line, condition)}
            </Flex>
          );
        })}
      </>
    );
  };

  const getSection = (section: EmergentContentSection, mode: string, side: string, sectionIndex: number) => {
    const sectionId = `${side}-${sectionIndex}`;
    const visibility =
      styledWordState.revealedSentences.some(sentence => sentence === sectionId) || isEditMode
        ? VisibilityState.visible
        : VisibilityState.hidden;
    const sectionColor = getSectionColor(section);

    switch (mode) {
      case SENTENCES_URL:
        return (
          <ContainerButton
            content={getStyledContainer(section, visibility) as JSX.Element}
            visibility={visibility}
            sectionId={sectionId}
            isAccessible={!styledWordState.imageTool.isVisible}
            color={sectionColor}
            onClick={handleClick}
            key={uuid()}
          />
        );
      case WORDS_AND_SENTENCES_URL:
        return (
          <ContainerButton
            content={getStyledContainer(section, VisibilityState.visible) as JSX.Element}
            visibility={VisibilityState.visible}
            sectionId={sectionId}
            isAccessible={!styledWordState.imageTool.isVisible}
            color={sectionColor}
            key={uuid()}
          />
        );
      default:
        return <></>;
    }
  };

  const renderContent = (side: ImageSide) => {
    const wordsAndSentences = getWordsAndSentencesContent(
      exemplarState.content as Array<WordsAndSentencesContent>,
      side
    );

    return wordsAndSentences?.map((section: EmergentContentSection | Image, index: number) => {
      if (section.type === 'section') {
        if (section.style?.includes('heading')) {
          return;
        }

        return getSection(section, mode, side, index);
      }

      const editImage = (hasPosition: boolean) => {
        return (
          <EditImage
            editMode={isEditMode && section.editable}
            src={getExemplarImageURL(section.uuid, relationshipsImages)}
            uuid={section.uuid}
            default_uuid={section.default_uuid}
            side={side}
            alt={'Emergent image'}
            styledWordState={styledWordState}
            uploadImage={handleUploadImage}
            hasPosition={hasPosition}
          />
        );
      };

      if (section.position) {
        const { x, y, width, height } = section.position;

        return (
          <ImageWithPositionWrapper width={width} top={y} left={x} height={height} key={index}>
            {editImage(true)}
          </ImageWithPositionWrapper>
        );
      }

      return (
        <MarginWrapper key={index} margin="16px 0px 30px 1px">
          {editImage(false)}
        </MarginWrapper>
      );
    });
  };

  const renderWordsScreen = () => {
    const title = (exemplarState.content[0] as WordsPageContent).title || exemplarState.title;
    const color = getSectionColor(title);

    return (
      <>
        <StyledExemplarHeading tabIndex={0} isAccessible={true} position="absolute" color={color}>
          {getStyledContainer(title, VisibilityState.visible)}
        </StyledExemplarHeading>
        <DrawingPanel mode={mode} isAccessible={!styledWordState.imageTool.isVisible} exemplarDimensions={dimensions} />
        {!exemplarState.is_default && renderWordsPageContent(exemplarState)}
      </>
    );
  };

  const renderInfoLabel = () => {
    if (isEditMode || ![WORDS_URL, SENTENCES_URL].includes(mode)) {
      return null;
    }

    return (
      <StyledInfoLabel data-testid="info-label">
        <InfoIcon />
        <span>Click a box to hide/reveal content</span>
      </StyledInfoLabel>
    );
  };

  const renderTitle = () => {
    const title = findEmergentExemplarTitle(exemplarState, urlToContentMap[mode]);
    const color = getSectionColor(title);

    return (
      <StyledExemplarHeading tabIndex={0} isAccessible={!styledWordState.imageTool.isVisible} color={color}>
        {getStyledContainer(title, VisibilityState.visible)}
      </StyledExemplarHeading>
    );
  };

  return (
    <>
      {mode !== WORDS_URL ? (
        <StyledEmergentContainer>
          {renderTitle()}
          <DrawingPanel
            mode={mode}
            isAccessible={!styledWordState.imageTool.isVisible}
            exemplarDimensions={dimensions}
          />
          <FlexWrapper key={uuid()}>
            <ContainerWrapper testId="left-side" key={uuid()} padding="0px 32px">
              {' '}
              {renderContent(ImageSide.left)}
            </ContainerWrapper>
            <ContainerWrapper testId="right-side" key={uuid()} padding="0px 32px">
              {' '}
              {renderContent(ImageSide.right)}
            </ContainerWrapper>
          </FlexWrapper>
        </StyledEmergentContainer>
      ) : (
        renderWordsScreen()
      )}
      {renderInfoLabel()}
      {isReplaceImageModalOpen && (
        <ReplaceImageModal
          exemplarId={exemplarId}
          default_uuid={selectedImage.default_uuid}
          defaultImageDimensions={selectedImage.dimensions}
          currentImageUUID={selectedImage.id}
          mode={mode}
          side={selectedImage.side}
          sectionId={selectedImage.sectionId}
          relatedImages={exemplarState.relationships.images}
          writingType={WritingType.exemplars}
          color={COLORS.debianRed}
          setRelationships={updateRelationships}
          replaceImage={replaceImage}
          onClose={() => {
            setReplaceImageModal(false);
          }}
        />
      )}
    </>
  );
};
